import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

/**
 * POS settlement for play-session Sales (ADR 0031). Ending a session hands its accrued charge to one
 * open {@link Sale}; the POS payment screen reads that open check here and completes it with a manual
 * payment, which settles the source session and drops it off the Floor board. Reaches the backend only
 * through the typed `apiClient` + `unwrap` seam (ADR 0012); scoping is server-side to the operator's
 * open-shift Venue, so the cache keys carry the saleId only.
 */
export type SaleState = 'open' | 'completed';

export type PaymentMethod = 'cash' | 'card_manual';

/** The domain flow a {@link Sale} was sourced from. `play_session` is v1; `booking` is the planned next source. */
export type SaleSourceType = 'play_session';

export interface SaleLine {
  id: string;
  /** `play` for the agreed tier, `extension` per SessionExtension. */
  kind: string;
  sourceId: string;
  label: string;
  /** Decimal string in the sale's currency (ADR 0021/0025). */
  amount: string;
  currency: string;
}

export interface Sale {
  id: string;
  /** The explicit source flow this Sale settles; `sourceId` is its originating id. */
  sourceType?: SaleSourceType;
  sourceId?: string;
  sourcePlaySessionId?: string;
  state: SaleState;
  currency: string;
  totalAmount: string;
  lines: SaleLine[];
  createdAt: string;
  completedAt?: string;
}

const saleKey = (saleId: string) => ['sales', saleId] as const;

const getSale = (saleId: string): Promise<Sale> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/sales/{id}', { params: { path: { id: saleId } } }),
  ) as Promise<Sale>;

const completeSale = (saleId: string, paymentMethod: PaymentMethod): Promise<Sale> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/sales/{id}/complete', {
      params: { path: { id: saleId } },
      body: { paymentMethod },
    }),
  ) as Promise<Sale>;

export function useSale(saleId: string, enabled = true) {
  return useQuery({
    queryKey: saleKey(saleId),
    queryFn: () => getSale(saleId),
    enabled: enabled && !!saleId,
    retry: false,
  });
}

export function useCompleteSale(saleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethod: PaymentMethod) => completeSale(saleId, paymentMethod),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: saleKey(saleId) });
      // Completion settles the source session server-side; refresh boards on the next Floor visit.
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}
